import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js'
import { api } from './api'
import type { AuthConfig, User } from './types'

let client: SupabaseClient | null = null
let clientFingerprint = ''
let syncEnabled = true
let syncEpoch = 0
let syncQueue: Promise<void> = Promise.resolve()
let activeSync: { token: string; promise: Promise<User> } | null = null
let cachedToken: string | null = null
let cachedUser: User | null = null
let manualSignoutEpoch: number | null = null

export class AuthSyncCancelledError extends Error {
  constructor() {
    super('Authentication sync was cancelled.')
    this.name = 'AuthSyncCancelledError'
  }
}

export function configureSupabase(config: AuthConfig) {
  if (config.provider !== 'supabase') return null
  if (!config.url || !config.publishable_key) throw new Error('Supabase authentication is not configured on this workspace.')
  const fingerprint = `${config.url}|${config.publishable_key}`
  if (!client || clientFingerprint !== fingerprint) {
    client = createClient(config.url, config.publishable_key, {
      auth: {
        storageKey: 'motion-summit-auth',
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    })
    clientFingerprint = fingerprint
    clearSyncCache()
    manualSignoutEpoch = null
  }
  return client
}

export function getSupabaseClient() {
  return client
}

export async function getInitialSupabaseSession(): Promise<Session | null> {
  if (!client) return null
  const { data, error } = await client.auth.getSession()
  if (error) throw error
  return data.session
}

export function resumeSupabaseSync() {
  if (syncEnabled) return
  syncEnabled = true
  syncEpoch += 1
}

export function isSupabaseSyncEnabled() {
  return syncEnabled
}

export function syncSupabaseSession(session: Session, options: { force?: boolean } = {}) {
  const token = session.access_token
  if (activeSync?.token === token) return activeSync.promise
  if (!options.force && cachedToken === token && cachedUser) return Promise.resolve(cachedUser)
  const epoch = syncEpoch
  const request = syncQueue.then(async () => {
    if (!syncEnabled || epoch !== syncEpoch) throw new AuthSyncCancelledError()
    if (!options.force && cachedToken === token && cachedUser) return cachedUser
    const user = await api.authSession(token)
    if (!syncEnabled || epoch !== syncEpoch) throw new AuthSyncCancelledError()
    cachedToken = token
    cachedUser = user
    return user
  })
  const promise = request.finally(() => {
    if (activeSync?.promise === promise) activeSync = null
  })
  activeSync = { token, promise }
  syncQueue = promise.then(() => undefined, () => undefined)
  return promise
}

export function subscribeToSupabaseAuth(onSynced: (user: User) => void, onError: (error: unknown) => void, onSignedOut: (automatic: boolean) => void) {
  if (!client) return () => undefined
  const { data: { subscription } } = client.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT') {
      const eventEpoch = syncEpoch
      globalThis.setTimeout(() => {
        if (!syncEnabled || eventEpoch === manualSignoutEpoch) { onSignedOut(false); return }
        syncEnabled = false
        syncEpoch += 1
        void syncQueue.catch(() => undefined).then(() => {
          clearSyncCache()
          return api.logout().catch(() => undefined)
        }).finally(() => onSignedOut(true))
      }, 0)
      return
    }
    if (event !== 'SIGNED_IN' && event !== 'TOKEN_REFRESHED') return
    globalThis.setTimeout(() => {
      if (!syncEnabled || !session) return
      void syncSupabaseSession(session, { force: event === 'TOKEN_REFRESHED' })
        .then(onSynced)
        .catch((error) => {
          if (!(error instanceof AuthSyncCancelledError)) onError(error)
        })
    }, 0)
  })
  return () => subscription.unsubscribe()
}

export async function logoutSupabase() {
  syncEnabled = false
  syncEpoch += 1
  manualSignoutEpoch = syncEpoch
  await syncQueue.catch(() => undefined)
  clearSyncCache()
  if (!client) return
  const { error } = await client.auth.signOut({ scope: 'local' })
  if (error) throw error
}

function clearSyncCache() {
  cachedToken = null
  cachedUser = null
  activeSync = null
}
